import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_gen-connect-11/artifacts/5jr50pac_gh%20logo.png";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/features", label: "Features" },
  { to: "/for-providers", label: "For Providers" },
  { to: "/about", label: "About" },
  { to: "/team", label: "Team" },
  { to: "/events", label: "Events" },
  { to: "/stories", label: "Stories" },
  { to: "/resources", label: "Resources" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={`sticky top-0 z-50 backdrop-blur-xl transition-all ${
        scrolled ? "bg-white/95 border-b border-slate-200 shadow-sm" : "bg-white/80"
      }`}
      data-testid="main-navbar"
    >
      <nav className="max-w-7xl mx-auto px-5 lg:px-8 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="navbar-logo-link">
          <div className="w-11 h-11 rounded-xl bg-navy flex items-center justify-center overflow-hidden">
            <img src={LOGO_URL} alt="Generational Health" className="w-full h-full object-contain scale-[1.5]" />
          </div>
          <div className="flex flex-col leading-tight whitespace-nowrap">
            <span className="font-display text-base text-navy tracking-tight" data-no-translate>generational <span className="text-teal">+Health</span></span>
            <span className="text-[10px] tracking-[0.18em] uppercase text-muted-ink font-semibold">Your mind. Your power.</span>
          </div>
        </Link>

        <div className="hidden xl:flex items-center gap-0.5 2xl:gap-1">
          {NAV_LINKS.map((link) => {
            const active = pathname === link.to;
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 2xl:px-4 py-2 text-sm font-semibold rounded-full whitespace-nowrap transition-colors ${
                  active
                    ? "text-teal bg-teal-light"
                    : "text-navy hover:text-teal hover:bg-slate-50"
                }`}
                data-testid={`navbar-link-${link.label.toLowerCase().replace(/ /g, "-")}`}
              >
                {link.label}
              </Link>
            );
          })}
        </div>

        <div className="hidden xl:flex items-center gap-2 shrink-0">
          <LanguageSwitcher testIdPrefix="navbar-lang" />
          <Link to="/donate" className="btn-ghost text-sm whitespace-nowrap" data-testid="navbar-donate-link">
            Donate
          </Link>
          <Link to="/download" className="btn-primary text-sm !py-2.5 !px-5 whitespace-nowrap" data-testid="navbar-download-button">
            Get the App
          </Link>
        </div>

        <div className="xl:hidden flex items-center gap-1">
          <LanguageSwitcher testIdPrefix="navbar-mobile-lang" />
          <button
            onClick={() => setOpen((v) => !v)}
            className="p-2 rounded-full text-navy hover:bg-slate-100 transition"
            aria-label="Toggle menu"
            data-testid="navbar-mobile-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="xl:hidden border-t border-slate-200 bg-white" data-testid="navbar-mobile-menu">
          <div className="px-5 py-5 space-y-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="block px-4 py-3 text-base font-semibold text-navy hover:bg-slate-50 rounded-xl transition"
                data-testid={`navbar-mobile-link-${link.label.toLowerCase().replace(/ /g, "-")}`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 flex flex-col gap-3">
              <Link to="/donate" className="btn-outline justify-center" data-testid="navbar-mobile-donate">
                Donate
              </Link>
              <Link to="/download" className="btn-primary justify-center" data-testid="navbar-mobile-download">
                Get the App
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
